/* Group navigation for the feed cleaner: desktop sidebar + mobile pills. */

import { Check } from "lucide-react";
import type { CleanerGroup } from "@/types/feedCleaner";

interface CleanerGroupNavProps {
  groups: CleanerGroup[];
  activeGroup: string;
  onSelectGroup: (name: string) => void;
  getItemAction: (id: string) => "queue" | "dismiss";
}

/** Mobile-only horizontal pill strip for group selection. */
export function CleanerGroupPills({
  groups,
  activeGroup,
  onSelectGroup,
  getItemAction,
}: CleanerGroupNavProps) {
  return (
    <div className="flex gap-2 overflow-x-auto scrollbar-none md:hidden pb-1">
      {groups.map((group) => {
        const queueCount = group.items.filter((i) => getItemAction(i.id) === "queue").length;
        const allQueued = queueCount === group.items.length;
        const isActive = activeGroup === group.name;

        return (
          <button
            key={group.name}
            className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent"
            }`}
            onClick={() => onSelectGroup(group.name)}
          >
            {group.name}
            <span className="tabular-nums">({group.items.length})</span>
            {allQueued && <Check className="size-3" />}
          </button>
        );
      })}
    </div>
  );
}

/** Desktop-only sidebar for group selection, rendered inside a flex container. */
export function CleanerGroupSidebar({
  groups,
  activeGroup,
  onSelectGroup,
  getItemAction,
}: CleanerGroupNavProps) {
  return (
    <div className="hidden md:block w-48 shrink-0 border-r border-border p-2 overflow-y-auto">
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-2 mb-1">
        Groups
      </p>
      {groups.map((group) => {
        const queueCount = group.items.filter((i) => getItemAction(i.id) === "queue").length;
        const dismissCount = group.items.length - queueCount;
        const isActive = activeGroup === group.name;

        return (
          <button
            key={group.name}
            className={`w-full text-left rounded-md p-2 mb-1 transition-colors ${
              isActive ? "bg-accent" : "hover:bg-accent/50"
            }`}
            onClick={() => onSelectGroup(group.name)}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium truncate">{group.name}</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span className="text-primary">{queueCount} keep</span>
              <span>·</span>
              <span>{dismissCount} remove</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
